import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import { EquipmentReservation } from '@models/warehouse.model';
import {
  useUpdateEquipmentReservation,
  useCancelEquipmentReservation,
} from '@hooks/api/warehouse/useEquipmentAssetMutations';
import { useEquipmentReservations } from '@hooks/api/warehouse';
import { useProjects } from '@hooks/api/projects';
import { useTasks } from '@hooks/api/task/useTasks';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface EquipmentReservationEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservation: EquipmentReservation | null;
  onSuccess?: () => void;
}

const formatDateDisplay = (date: string | Date) =>
  format(new Date(date), 'MMM dd, yyyy h:mm a');

const toDateString = (date: string | Date) => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

const toTimeString = (date: string | Date) => {
  const d = new Date(date);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const EquipmentReservationEditModal: React.FC<EquipmentReservationEditModalProps> = ({
  isOpen,
  onClose,
  reservation,
  onSuccess,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('23:59');
  const [notes, setNotes] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateReservation = useUpdateEquipmentReservation();
  const cancelReservation = useCancelEquipmentReservation();

  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { tasks, loading: tasksLoading } = useTasks(selectedProjectId || undefined);

  // Fetch other reservations for this asset to detect conflicts
  const { data: assetReservations = [] } = useEquipmentReservations(
    reservation?.assetId || undefined,
  );

  // Pre-fill form from reservation data
  useEffect(() => {
    if (isOpen && reservation) {
      setStartDate(toDateString(reservation.startDate));
      setStartTime(toTimeString(reservation.startDate));
      setEndDate(toDateString(reservation.endDate));
      setEndTime(toTimeString(reservation.endDate));
      setNotes(reservation.notes || '');
      setTaskId(reservation.taskId);
      setShowDeleteConfirm(false);

      // Find the project for this task
      if (reservation.task?.projectId) {
        setSelectedProjectId(reservation.task.projectId);
      }
    }
  }, [isOpen, reservation]);

  // Reset task when project changes (only if user manually changes project)
  useEffect(() => {
    if (selectedProjectId && reservation?.task?.projectId !== selectedProjectId) {
      setTaskId('');
    }
  }, [selectedProjectId, reservation?.task?.projectId]);

  // Combine date + time into ISO strings
  const combinedStart = useMemo(() => {
    if (!startDate) return null;
    return new Date(`${startDate}T${startTime || '00:00'}:00`);
  }, [startDate, startTime]);

  const combinedEnd = useMemo(() => {
    if (!endDate) return null;
    return new Date(`${endDate}T${endTime || '23:59'}:00`);
  }, [endDate, endTime]);

  // Check for overlapping reservations (exclude current)
  const overlapWarning = useMemo(() => {
    if (!combinedStart || !combinedEnd || !reservation) return null;
    const otherReservations = assetReservations.filter(
      (r) => r.reservationId !== reservation.reservationId,
    );
    if (otherReservations.length === 0) return null;

    const overlapping = otherReservations.filter((r) => {
      const rStart = new Date(r.startDate);
      const rEnd = new Date(r.endDate);
      return rStart <= combinedEnd && rEnd >= combinedStart;
    });
    if (overlapping.length === 0) return null;
    return overlapping;
  }, [combinedStart, combinedEnd, assetReservations, reservation]);

  const canSubmit =
    taskId &&
    startDate &&
    endDate &&
    combinedStart &&
    combinedEnd &&
    combinedEnd > combinedStart &&
    !overlapWarning &&
    !updateReservation.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || !reservation || !combinedStart || !combinedEnd) return;

    await updateReservation.mutateAsync({
      reservationId: reservation.reservationId,
      taskId,
      startDate: combinedStart.toISOString(),
      endDate: combinedEnd.toISOString(),
      notes: notes.trim() || undefined,
    });

    onSuccess?.();
    onClose();
  };

  const handleDelete = async () => {
    if (!reservation) return;

    await cancelReservation.mutateAsync(reservation.reservationId);
    onSuccess?.();
    onClose();
  };

  const projectOptions: SelectOption[] = projects.map((p) => ({
    value: p.projectId || '',
    label: p.name,
  }));

  const taskOptions: SelectOption[] = tasks
    .filter((t) => t.taskId)
    .map((t) => ({
      value: t.taskId!,
      label: t.name,
    }));

  if (!reservation) return null;

  // Checkout reservations should not be editable
  if (reservation.isCheckoutReservation) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Reservation"
      confirmText="Update Reservation"
      handleConfirm={handleSubmit}
      isLoading={updateReservation.isPending}
      disableConfirm={!canSubmit}
      size="medium">
      <div className="flex flex-col gap-5">
        {/* Header Info */}
        <div className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
          <EditIcon className="text-indigo-500" sx={{ fontSize: 18 }} />
          <p className="text-sm text-indigo-700">
            Edit the reservation details below. Changes will be validated against existing reservations.
          </p>
        </div>

        {/* Overlap Warning */}
        {overlapWarning && overlapWarning.length > 0 && (
          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
            <WarningAmberIcon className="text-red-500 mt-0.5 flex-shrink-0" sx={{ fontSize: 18 }} />
            <div>
              <p className="text-sm font-medium text-red-700">Conflict Detected</p>
              <p className="text-sm text-red-600 mt-1">
                This reservation overlaps with {overlapWarning.length} existing reservation(s):
              </p>
              <ul className="mt-1 space-y-0.5">
                {overlapWarning.map((r) => (
                  <li key={r.reservationId} className="text-xs text-red-600">
                    • {r.task?.name || 'Unknown task'} ({formatDateDisplay(r.startDate)} — {formatDateDisplay(r.endDate)})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Project Selection */}
        <FormGroup label="Project" htmlFor="editProject">
          <ReactSelect
            options={projectOptions}
            value={
              selectedProjectId
                ? projectOptions.find((o) => o.value === selectedProjectId) || null
                : null
            }
            onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
              if (option && !Array.isArray(option) && 'value' in option) {
                setSelectedProjectId(option.value as string);
              } else {
                setSelectedProjectId('');
              }
            }}
            placeholder="Select a project..."
            isSearchable
            isClearable
            isLoading={projectsLoading}
          />
        </FormGroup>

        {/* Task Selection */}
        {selectedProjectId && (
          <FormGroup label="Task" htmlFor="editTask">
            <ReactSelect
              options={taskOptions}
              value={
                taskId
                  ? taskOptions.find((o) => o.value === taskId) || null
                  : null
              }
              onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                if (option && !Array.isArray(option) && 'value' in option) {
                  setTaskId(option.value as string);
                } else {
                  setTaskId('');
                }
              }}
              placeholder={tasksLoading ? 'Loading tasks...' : 'Select a task...'}
              isSearchable
              isClearable
              isLoading={tasksLoading}
            />
          </FormGroup>
        )}

        {/* Date & Time Range */}
        <div className="grid grid-cols-2 gap-4">
          <FormGroup label="Start Date" htmlFor="editStartDate">
            <Input
              id="editStartDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Start Time" htmlFor="editStartTime">
            <Input
              id="editStartTime"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="End Date" htmlFor="editEndDate">
            <Input
              id="editEndDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
            />
          </FormGroup>
          <FormGroup label="End Time" htmlFor="editEndTime">
            <Input
              id="editEndTime"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </FormGroup>
        </div>

        {/* Notes */}
        <FormGroup label="Notes" htmlFor="editNotes">
          <textarea
            id="editNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reason for reservation or any additional notes..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
            rows={3}
          />
        </FormGroup>

        {/* Delete Reservation */}
        <div className="pt-2 border-t border-gray-200">
          {!showDeleteConfirm ? (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 transition-colors"
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              Cancel this reservation
            </button>
          ) : (
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700 flex-1">
                Are you sure you want to cancel this reservation?
              </p>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                No
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={cancelReservation.isPending}
                className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {cancelReservation.isPending ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EquipmentReservationEditModal;
